import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { submitManualPayment } from '../api/payments.api';
import { clearRegistrationPhone, getRegistrationPhone } from '../utils/registration';
import { useAuth } from '../context/AuthContext';
import { AuthResponse } from '../api/auth.api';

const paymentMethods = ['bKash', 'Nagad', 'Rocket', 'Dutch Bangla Bank'] as const;

const PaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, setPending } = useAuth();
  const phone = getRegistrationPhone();
  const [paymentMethod, setPaymentMethod] = useState<typeof paymentMethods[number] | ''>('');
  const [transactionId, setTransactionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!phone) {
      setError('Registration phone is missing. Please register again.');
      return;
    }
    if (!paymentMethod || !transactionId.trim()) {
      setError('Payment method and transaction ID are required.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const response = await submitManualPayment({
        phone,
        payment_method: paymentMethod,
        transaction_id: transactionId.trim(),
      });
      
      // If payment submission returns token and user, automatically log them in
      if (response.data && response.data.token && response.data.user) {
        login({
          token: response.data.token,
          user: response.data.user
        } as AuthResponse);
        clearRegistrationPhone();
        setSuccess(true);
        setTimeout(() => navigate('/dashboard'), 1200);
      } else {
        // Fallback: set pending status if no token returned
        setPending();
        clearRegistrationPhone();
        setSuccess(true);
        setTimeout(() => navigate('/dashboard'), 1200);
      }
    } catch (err) {
      setError('Payment submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!phone) {
    return (
      <div className="container py-5">
        <div className="card card-surface">
          <div className="card-body text-center py-5">
            <h1 className="h4 mb-3">Manual Payment</h1>
            <p className="text-muted-soft mb-4">
              We could not find your registration phone number.
            </p>
            <button className="btn btn-arena" onClick={() => navigate('/register')}>
              Go to Registration
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="mb-3">
            <Link to="/register" className="btn btn-outline-secondary btn-sm">
              <i className="bi bi-arrow-left me-1"></i>
              Back to Registration
            </Link>
          </div>
          <div className="mb-4 text-center">
            <h1 className="h4 mb-2">Manual Payment</h1>
            <p className="text-muted-soft mb-0">Submit your transaction ID to complete registration.</p>
          </div>

          <div className="card card-surface mb-3">
            <div className="card-body">
              <div className="mb-3">
                  <h2 className="h6 text-uppercase mb-1">Mobile Wallet Payment Details</h2>
                  <p className="text-muted-soft small mb-0">
                    Please send the registration fee to the above number using your selected payment method.
                  </p>
              </div>
              <div className="row g-3 mt-2">
                <div className="col-md-6">
                  <div className="border rounded p-3 h-100">
                    <div className="fw-semibold">bKash / Nagad</div>
                    <div className="h5 mb-0">01758059313</div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="border rounded p-3 h-100">
                    <div className="fw-semibold">Rocket (DBBL)</div>
                    <div className="h5 mb-0">017580593130</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card card-surface mb-4">
            <div className="card-body">
              <h2 className="h6 text-uppercase mb-1">Dutch Bangla Bank Account Information</h2>
              <p className="text-muted-soft small mb-3">
                Dutch Bangla Bank users must submit the transaction/reference ID.
              </p>
              <div className="row g-2">
                <div className="col-sm-6">
                  <div className="small text-muted-soft">Account Name</div>
                  <div className="fw-semibold">SPORTS ZONE BD</div>
                </div>
                <div className="col-sm-6">
                  <div className="small text-muted-soft">Account Number</div>
                  <div className="fw-semibold">3081100010893</div>
                </div>
                <div className="col-sm-6">
                  <div className="small text-muted-soft">Account Type</div>
                  <div className="fw-semibold">Current</div>
                </div>
                <div className="col-sm-6">
                  <div className="small text-muted-soft">Area</div>
                  <div className="fw-semibold">Dhaka North</div>
                </div>
                <div className="col-sm-6">
                  <div className="small text-muted-soft">Branch</div>
                  <div className="fw-semibold">Shewrapara</div>
                </div>
                <div className="col-sm-6">
                  <div className="small text-muted-soft">Routing Number</div>
                  <div className="fw-semibold">090264272</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card card-surface shadow-sm">
            <div className="card-body p-4">

              {success ? (
                <div className="alert alert-warning text-center mb-0">
                  <strong>Payment Submitted!</strong>
                  <br />
                  Your payment has been submitted successfully.
                  <br />
                  <small className="text-muted">
                    Your registration is pending admin approval. You will be notified once approved.
                  </small>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="d-grid gap-3">
                  <div>
                    <label className="form-label">Phone Number</label>
                    <input type="text" className="form-control" value={phone} readOnly disabled />
                  </div>
                  <div>
                    <label className="form-label">
                      Payment Method <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={paymentMethod}
                      onChange={(event) =>
                        setPaymentMethod(event.target.value as typeof paymentMethods[number])
                      }
                    >
                      <option value="">Select payment method</option>
                      {paymentMethods.map((method) => (
                        <option key={method} value={method}>
                          {method}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="form-label">
                      Transaction ID <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={transactionId}
                      onChange={(event) => setTransactionId(event.target.value)}
                    />
                  </div>
                  {error && <div className="alert alert-danger mb-0">{error}</div>}
                  <button type="submit" className="btn btn-arena" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Payment'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
